import React, { useState } from 'react';
import { X, Plus, Trash2, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { RegistryEvent } from '../types/registry';

interface Props {
  registryId: string;
  events: RegistryEvent[];
  onClose: () => void;
  onUpdate: () => void;
}

export default function EditEventsModal({ registryId, events, onClose, onUpdate }: Props) {
  const [eventsList, setEventsList] = useState<Omit<RegistryEvent, 'id' | 'registry_id' | 'created_at'>[]>(
    events.map(event => ({
      name: event.name,
      date: event.date,
      start_time: event.start_time,
      end_time: event.end_time,
      venue: event.venue,
      address: event.address,
      dress_code: event.dress_code,
      parking_info: event.parking_info,
      max_attendees: event.max_attendees,
      special_instructions: event.special_instructions
    }))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [newEvent, setNewEvent] = useState({
    name: '',
    date: '',
    start_time: '',
    end_time: '',
    venue: '',
    address: '',
    dress_code: '',
    parking_info: '',
    max_attendees: '',
    special_instructions: ''
  });

  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (newEvent.name && newEvent.date && newEvent.start_time && newEvent.venue && newEvent.address) {
      setEventsList([...eventsList, {
        ...newEvent,
        max_attendees: newEvent.max_attendees ? parseInt(newEvent.max_attendees) : null,
        end_time: newEvent.end_time || null,
        dress_code: newEvent.dress_code || null,
        parking_info: newEvent.parking_info || null,
        special_instructions: newEvent.special_instructions || null
      }]);
      setNewEvent({
        name: '',
        date: '',
        start_time: '',
        end_time: '',
        venue: '',
        address: '',
        dress_code: '',
        parking_info: '',
        max_attendees: '',
        special_instructions: ''
      });
    }
  };

  const removeEvent = (index: number) => {
    setEventsList(eventsList.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Delete existing events
      const { error: deleteError } = await supabase
        .from('registry_events')
        .delete()
        .eq('registry_id', registryId);

      if (deleteError) throw deleteError;

      // Insert new events
      const { error: insertError } = await supabase
        .from('registry_events')
        .insert(
          eventsList.map(event => ({
            ...event,
            registry_id: registryId
          }))
        );

      if (insertError) throw insertError;

      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating events:', err);
      setError('Failed to update events');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
      <div className="card-pastel p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 font-poppins">Manage Events</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div className="space-y-4">
            {eventsList.map((event, index) => (
              <div
                key={index}
                className="bg-white/60 p-4 rounded-lg space-y-2"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-800 font-poppins">{event.name}</h4>
                  <button
                    onClick={() => removeEvent(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 font-poppins">
                  <div>
                    <span className="font-medium">Date:</span> {event.date}
                  </div>
                  <div>
                    <span className="font-medium">Time:</span> {event.start_time}
                    {event.end_time && ` - ${event.end_time}`}
                  </div>
                  <div>
                    <span className="font-medium">Venue:</span> {event.venue}
                  </div>
                  <div>
                    <span className="font-medium">Address:</span> {event.address}
                  </div>
                  {event.dress_code && (
                    <div>
                      <span className="font-medium">Dress Code:</span> {event.dress_code}
                    </div>
                  )}
                  {event.max_attendees && (
                    <div>
                      <span className="font-medium">Max Attendees:</span> {event.max_attendees}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-white/40 pt-6">
            <h4 className="text-lg font-medium text-gray-800 mb-4 font-poppins">Add New Event</h4>
            <form onSubmit={handleAddEvent} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Start Time *
                  </label>
                  <input
                    type="time"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Venue *
                  </label>
                  <input
                    type="text"
                    value={newEvent.venue}
                    onChange={(e) => setNewEvent({ ...newEvent, venue: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Address *
                  </label>
                  <input
                    type="text"
                    value={newEvent.address}
                    onChange={(e) => setNewEvent({ ...newEvent, address: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Dress Code
                  </label>
                  <input
                    type="text"
                    value={newEvent.dress_code}
                    onChange={(e) => setNewEvent({ ...newEvent, dress_code: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Max Attendees
                  </label>
                  <input
                    type="number"
                    value={newEvent.max_attendees}
                    onChange={(e) => setNewEvent({ ...newEvent, max_attendees: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    min="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                  Parking Information
                </label>
                <textarea
                  value={newEvent.parking_info}
                  onChange={(e) => setNewEvent({ ...newEvent, parking_info: e.target.value })}
                  className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                  Special Instructions
                </label>
                <textarea
                  value={newEvent.special_instructions}
                  onChange={(e) => setNewEvent({ ...newEvent, special_instructions: e.target.value })}
                  className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                  rows={2}
                />
              </div>

              <button
                type="submit"
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 transition-colors font-poppins"
              >
                <Plus className="w-5 h-5" />
                <span>Add Event</span>
              </button>
            </form>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-white/40">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-poppins"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || eventsList.length === 0}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 font-poppins"
            >
              {loading ? 'Saving...' : 'Save Events'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}